<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useTheme } from '@/composables/useTheme';
import { useI18n } from 'vue-i18n';

const { isDark, toggle } = useTheme();
const { locale, t } = useI18n({ useScope: 'global' });

const LOCALES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
];

const showLangMenu = ref(false);

function setLocale(code: string) {
  locale.value = code;
  localStorage.setItem('locale', code);
  showLangMenu.value = false;
}

const currentLocale = () =>
  LOCALES.find((l) => l.code === locale.value) ?? LOCALES[0];

// ── Hero slider ──────────────────────────────────────────────────
const SLIDES = [
  {
    url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80',
    caption: 'Encaissements fluides, partout',
  },
  {
    url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80',
    caption: "Gérez vos commerces en un coup d'œil",
  },
  {
    url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&q=80',
    caption: "L'expérience client réinventée",
  },
];

const current = ref(0);
let timer: ReturnType<typeof setInterval>;

function goTo(i: number) {
  current.value = (i + SLIDES.length) % SLIDES.length;
}

onMounted(() => {
  timer = setInterval(() => goTo(current.value + 1), 5000);
});
onUnmounted(() => clearInterval(timer));
</script>

<template>
  <div class="auth" :class="{ 'auth--dark': isDark }">
    <!-- Boutons flottants -->
    <div class="auth__actions">
      <div class="auth__lang-wrap">
        <button
          class="auth__action-btn"
          @click="showLangMenu = !showLangMenu"
          aria-label="Changer la langue"
        >
          <span class="auth__lang-flag">{{ currentLocale().flag }}</span>
        </button>
        <Transition name="lang-drop">
          <div v-if="showLangMenu" class="auth__lang-menu">
            <button
              v-for="l in LOCALES"
              :key="l.code"
              class="auth__lang-item"
              :class="{ 'auth__lang-item--active': locale === l.code }"
              @click="setLocale(l.code)"
            >
              <span>{{ l.flag }}</span
              ><span>{{ l.label }}</span>
            </button>
          </div>
        </Transition>
      </div>
      <button
        class="auth__action-btn"
        @click="toggle"
        :aria-label="isDark ? 'Mode clair' : 'Mode sombre'"
      >
        <i :class="isDark ? 'pi pi-sun' : 'pi pi-moon'" />
      </button>
    </div>

    <!-- Panneau gauche -->
    <div class="auth__left">
      <div class="auth__brand">
        <h1 class="auth__brand-name">{{ t('auth.brand') }}</h1>
        <p class="auth__brand-sub">{{ t('auth.brandSub') }}</p>
      </div>

      <div class="auth__card">
        <slot />
      </div>

      <footer class="auth__footer">
        <RouterLink to="/terms" class="auth__footer-link">{{
          t('auth.footer.terms')
        }}</RouterLink>
        <p class="auth__footer-copy">{{ t('auth.footer.copy') }}</p>
      </footer>
    </div>

    <!-- Panneau droit : slider -->
    <div class="auth__right" aria-hidden="true">
      <TransitionGroup name="slide-fade" tag="div" class="auth__slides">
        <div
          v-for="(slide, i) in SLIDES"
          :key="i"
          v-show="current === i"
          class="auth__slide"
          :style="{ backgroundImage: `url('${slide.url}')` }"
        >
          <div class="auth__slide-overlay" />
          <p class="auth__slide-caption">{{ slide.caption }}</p>
        </div>
      </TransitionGroup>

      <!-- Avatars décoratifs flottants -->
      <div class="auth__avatars">
        <div class="auth__avatar-ring auth__avatar-ring--1">
          <div class="auth__av auth__av--1">MB</div>
          <div class="auth__av auth__av--2">SA</div>
          <div class="auth__av auth__av--3">JL</div>
        </div>
        <div class="auth__avatar-ring auth__avatar-ring--2">
          <div class="auth__av auth__av--4">KD</div>
          <div class="auth__av auth__av--5">NP</div>
        </div>
        <div class="auth__avatar-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255,255,255,0.9)"
            stroke-width="1.5"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <div class="auth__avatar-stats">
          <div class="auth__av-stat">
            <span class="auth__av-stat-num">+2.4k</span>
            <span class="auth__av-stat-lbl">Clients actifs</span>
          </div>
          <div class="auth__av-stat">
            <span class="auth__av-stat-num">98%</span>
            <span class="auth__av-stat-lbl">Satisfaction</span>
          </div>
        </div>
      </div>

      <!-- Dots -->
      <div class="auth__dots">
        <button
          v-for="(_, i) in SLIDES"
          :key="i"
          class="auth__dot"
          :class="{ 'auth__dot--active': current === i }"
          @click="goTo(i)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Root ─────────────────────────────────────────────────────── */
.auth {
  display: flex;
  width: 100dvw;
  height: 100dvh;
  overflow: hidden;
  background-color: #ffffff;
  font-family: 'Hanken Grotesk', sans-serif;
  transition: background-color 0.3s ease;
}
.auth--dark {
  background-color: var(--nxt-background);
}

/* ── Floating actions ─────────────────────────────────────────── */
.auth__actions {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 50;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.auth__action-btn {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.05rem;
  color: #191c1d;
  transition: transform 0.2s ease;
}
.auth__action-btn:hover {
  transform: scale(1.08);
}
[data-theme='dark'] .auth__action-btn {
  background: rgba(30, 32, 34, 0.85);
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--nxt-on-surface);
}

/* ── Lang menu ─────────────────────────────────────────────────── */
.auth__lang-wrap {
  position: relative;
}
.auth__lang-flag {
  font-size: 1.2rem;
  line-height: 1;
}
.auth__lang-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 160px;
}
[data-theme='dark'] .auth__lang-menu {
  background: rgba(30, 32, 34, 0.96);
  border-color: rgba(255, 255, 255, 0.1);
}
.auth__lang-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 1rem;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-family: inherit;
  color: #191c1d;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;
}
[data-theme='dark'] .auth__lang-item {
  color: var(--nxt-on-surface);
}
.auth__lang-item:hover {
  background: rgba(0, 0, 0, 0.06);
}
.auth__lang-item--active {
  font-weight: 600;
  color: var(--nxt-primary);
}

.lang-drop-enter-active,
.lang-drop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.lang-drop-enter-from,
.lang-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Left panel ────────────────────────────────────────────────── */
.auth__left {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.75rem;
  padding: 2rem 1.25rem;
  height: 100%;
  overflow-y: auto;
  /* mobile: push below brand on small screens */
}

/* ── Branding ──────────────────────────────────────────────────── */
.auth__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}
.auth__brand-name {
  margin: 0;
  /* Responsive: smaller on mobile */
  font-size: clamp(1.75rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--nxt-primary);
  letter-spacing: -0.02em;
  line-height: 1;
}
.auth__brand-sub {
  margin: 0;
  font-size: clamp(0.5625rem, 2vw, 0.75rem);
  font-weight: 600;
  letter-spacing: 0.18em;
  color: #5f5e5f;
  text-transform: uppercase;
  white-space: nowrap;
}

/* ── Card ──────────────────────────────────────────────────────── */
.auth__card {
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  border-radius: 1.25rem;
  padding: 2rem 1.5rem;
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease;
}
[data-theme='dark'] .auth__card {
  background: rgba(30, 32, 34, 0.75);
  border-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
}

/* ── Footer ────────────────────────────────────────────────────── */
.auth__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}
.auth__footer-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: #5f5e5f;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: rgba(95, 94, 95, 0.3);
}
.auth__footer-link:hover {
  color: var(--nxt-primary);
}
.auth__footer-copy {
  margin: 0;
  font-size: 0.75rem;
  color: #938282;
  opacity: 0.6;
}

/* ── Right panel (slider) ──────────────────────────────────────── */
.auth__right {
  display: none;
  flex: 1;
  height: 100%;
  position: relative;
  overflow: hidden;
}
@media (min-width: 1024px) {
  .auth__right {
    display: block;
  }
}

.auth__slides {
  width: 100%;
  height: 100%;
  position: relative;
}
.auth__slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: opacity 0.8s ease;
}
.auth__slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0.15) 50%,
    rgba(0, 0, 0, 0.06) 100%
  );
}
.auth__slide-caption {
  position: absolute;
  bottom: 5rem;
  left: 2rem;
  right: 2rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.4);
  line-height: 1.3;
}

/* ── Floating avatars ──────────────────────────────────────────── */
.auth__avatars {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  width: 320px;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Base avatar style */
.auth__av {
  position: absolute;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 2px solid rgba(255, 255, 255, 0.45);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.04em;
}

/* Ring 1 — 3 avatars at ~120px radius */
.auth__avatar-ring--1 {
  position: absolute;
  inset: 0;
  animation: ring-spin-1 18s linear infinite;
}
.auth__avatar-ring--1 .auth__av {
  animation: counter-spin 18s linear infinite;
}

.auth__av--1 {
  top: 0px;
  left: 50%;
  transform: translate(-50%, -100%);
}
.auth__av--2 {
  bottom: 20px;
  left: 12px;
}
.auth__av--3 {
  bottom: 20px;
  right: 12px;
}

/* Ring 2 — 2 avatars at ~155px radius */
.auth__avatar-ring--2 {
  position: absolute;
  inset: 0;
  animation: ring-spin-2 26s linear infinite reverse;
}
.auth__avatar-ring--2 .auth__av {
  animation: counter-spin-2 26s linear infinite;
}

.auth__av--4 {
  top: 60px;
  right: -10px;
  width: 2.75rem;
  height: 2.75rem;
  font-size: 0.625rem;
}
.auth__av--5 {
  bottom: 50px;
  left: -10px;
  width: 2.75rem;
  height: 2.75rem;
  font-size: 0.625rem;
}

/* Tints per avatar */
.auth__av--1 {
  background: rgba(255, 255, 255, 0.22);
}
.auth__av--2 {
  background: rgba(255, 255, 255, 0.14);
}
.auth__av--3 {
  background: rgba(255, 255, 255, 0.18);
}
.auth__av--4 {
  background: rgba(255, 255, 255, 0.12);
}
.auth__av--5 {
  background: rgba(255, 255, 255, 0.16);
}

@keyframes ring-spin-1 {
  to {
    transform: rotate(360deg);
  }
}
@keyframes counter-spin {
  to {
    transform: rotate(-360deg) translate(-50%, -100%);
  }
}
@keyframes ring-spin-2 {
  to {
    transform: rotate(-360deg);
  }
}
@keyframes counter-spin-2 {
  to {
    transform: rotate(360deg);
  }
}

/* Centre icon */
.auth__avatar-center {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(16px);
  border: 2px solid rgba(255, 255, 255, 0.35);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

/* Stats pills */
.auth__avatar-stats {
  position: absolute;
  bottom: -3.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.75rem;
}
.auth__av-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.875rem;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}
.auth__av-stat-num {
  font-size: 1.0625rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}
.auth__av-stat-lbl {
  font-size: 0.5625rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 0.2rem;
}

/* Dots */
.auth__dots {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
}
.auth__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.45);
  border: none;
  cursor: pointer;
  transition:
    width 0.3s ease,
    background 0.3s ease;
  padding: 0;
}
.auth__dot--active {
  width: 1.5rem;
  background: #fff;
}

/* Slide transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.8s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}

/* ── Auth page route transition ────────────────────────────────── */
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.auth-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.auth-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Mobile adjustments ────────────────────────────────────────── */
@media (max-width: 480px) {
  .auth__left {
    padding: 1.5rem 1rem;
    gap: 1.25rem;
  }
  .auth__card {
    padding: 1.5rem 1.125rem;
    border-radius: 1rem;
  }
  .auth__actions {
    top: 1rem;
    right: 1rem;
  }
  .auth__action-btn {
    width: 2.375rem;
    height: 2.375rem;
    font-size: 0.95rem;
  }
}
</style>
